import { useState, useRef, useEffect, memo } from 'react';
import { Link, useLocation } from 'wouter';
import { useSearch } from 'wouter/use-browser-location';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bell,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  KeyRound,
  LogOut,
  Menu,
  MoreVertical,
  Sparkles,
  User as UserIcon,
} from 'lucide-react';
import { cn } from '@shared/lib';
import { getCurrentUser, logout } from '@services/storage';
import { useLiveNotifications } from '@services/notifications';
import { useDateTimeFormatter } from '@services';
import { useTranslation } from '@infra/i18n';
import type { TranslationParams } from '@infra/i18n';
import { getUserRoles } from '@infra/auth';
import { useMeAccess } from '@services/access';
import { useAccountPaths } from './useAccountPaths';
import type { Notification, NotificationSeverity, UserRole } from '@shared/types';
import { Button, Avatar, AvatarFallback } from '@shared/ui';
import {
  brandColors,
  insucoachType,
  landingGradients,
  USER_ROLE_CLAIMS,
  brandRgba,
} from '@shared/constants';
import { SandboxNotice } from '@shared/ui/layout';
import { LanguageToggle } from '@shared/ui/layout';
import { LanguageOptions } from '@shared/ui/layout';
import { useBreadcrumbEntityTitle, useManagementBreadcrumb } from '@shared/ui/layout';

interface TopBarProps {
  onOpenMobile?: () => void;
}

/** One breadcrumb level. The last crumb is the current page and carries no href. */
type Crumb = { label: string; href?: string };

const severityDot: Record<NotificationSeverity, string> = {
  info: 'bg-primary',
  warning: 'bg-tone-caution-solid',
  error: 'bg-destructive',
  success: 'bg-tone-positive-solid',
};

// Sections whose URL segment carries no list route of its own — `/user/sessions/:id/report`
// exists but `/user/sessions` redirects to /404. Send the breadcrumb parent to the page that
// actually lists them instead.
const sectionHrefOverrides: Record<string, string> = {
  sessions: 'analytics',
};

/** Where a section crumb links: `/user` and `/admin` are redirects, not pages. */
const SECTION_LANDING_PAGE = 'dashboard';

// ─── Page meta map ────────────────────────────────────────────────────────────
function usePageMeta(location: string) {
  const { t } = useTranslation();

  const segments = location.split('/').filter(Boolean);
  const page = segments[1] ?? 'dashboard';
  const isOrganizationTeams =
    segments[0] === 'admin' && page === 'organizations' && segments[3] === 'teams';
  // Resolve ONLY the current page's title. The previous full-map version called t() for
  // ~10 namespaces every render → eager-loaded them all + a namespaceLoaded event storm.
  const pageTitle = ((): string => {
    switch (page) {
      case 'dashboard':
        return t('common:nav.dashboard');
      case 'personas':
        return t('personas:title');
      case 'scenarios':
        return t('scenarios:title');
      case 'create-roleplay':
        return t('roleplay:create.title');
      case 'roleplay':
        return t('roleplay:roleplay.sessionTitle');
      case 'sessions':
        return t('reports:title');
      case 'analytics':
        return t('analytics:title');
      case 'profile':
        return t('settings:tabs.profile');
      case 'users':
        return t('common:nav.users');
      case 'teams':
        return t('common:nav.teams');
      case 'team':
        return t('common:nav.myTeam');
      case 'coaching-hub':
        return t('coaching:hub.title');
      case 'stage-collection':
        return t('stages:collection.title');
      case 'settings':
        if (location.includes('/settings/stage-collection')) {
          return t('stages:collection.title');
        }
        if (location.includes('/settings/stages')) {
          return t('stages:admin.title');
        }
        if (location.includes('/settings/skills')) {
          return t('skills:admin.title');
        }
        if (location.includes('/settings/observable-signals')) {
          return t('observable-signals:admin.title');
        }
        if (location.includes('/settings/system-configuration')) {
          return t('system-configuration:admin.title');
        }
        if (location.includes('/settings/ice-server')) {
          return t('ice-server:admin.title');
        }
        return t('settings:title');
      case 'usage':
        return t('common:nav.roleplayUsage');
      case 'entitlements':
        return t('common:nav.entitlements');
      case 'organizations':
        return t(isOrganizationTeams ? 'common:nav.teams' : 'common:nav.organizations');
      case 'learning':
        return t('common:nav.learning');
      case 'bookings':
        return t('common:nav.bookings');
      case 'notifications':
        return t('common:notificationsPage.title');
      default:
        return page.charAt(0).toUpperCase() + page.slice(1);
    }
  })();
  const section = location.split('/').filter(Boolean)[0] ?? '';
  const sectionLabel =
    section === 'admin'
      ? t('common:topbar.sectionAdmin')
      : section === 'user'
        ? t('common:topbar.sectionWorkspace')
        : '';

  // Action level for "create"/"edit" sub-routes (e.g. /user/personas/new,
  // /user/personas/:uuid/edit). This is a crumb of its own — it sits AFTER the
  // entity name rather than replacing it, which is what used to drop the
  // persona's name from `Personas > {Name} > Edit Persona`.
  const subSegment = segments[2];
  const lastSegment = segments[segments.length - 1];
  const isStages = location.includes('/stages');
  const isSkills = location.includes('/skills');
  const isSignals = location.includes('/observable-signals');
  const targetPageKey = isStages
    ? 'stages'
    : isSkills
      ? 'skills'
      : isSignals
        ? 'observable-signals'
        : page;
  const isCreateRoute = subSegment === 'new' || lastSegment === 'new' || lastSegment === 'create';
  const isEditRoute = lastSegment === 'edit';
  // Learner import is its own page rather than a dialog, so it needs a crumb of its own.
  const isImportRoute = subSegment === 'import';

  // Resolve only the matching branch: touching an unloaded namespace triggers a preload
  // fetch, so building every admin taxonomy label on each render would load them all.
  const actionTitle =
    !isCreateRoute && !isEditRoute && !isImportRoute
      ? undefined
      : targetPageKey === 'personas'
        ? t(isCreateRoute ? 'personas:create.title' : 'personas:edit.title')
        : targetPageKey === 'scenarios'
          ? t(isCreateRoute ? 'roleplay:create.title' : 'scenarios:admin.editScenarioTitle')
          : targetPageKey === 'stages'
            ? t(isCreateRoute ? 'stages:admin.modalCreateTitle' : 'stages:admin.modalEditTitle')
            : targetPageKey === 'skills'
              ? t(isCreateRoute ? 'skills:admin.modalCreateTitle' : 'skills:admin.modalEditTitle')
              : targetPageKey === 'observable-signals'
                ? t(
                    isCreateRoute
                      ? 'observable-signals:admin.modalCreateTitle'
                      : 'observable-signals:admin.modalEditTitle'
                  )
                : undefined;

  const parentCrumb: Crumb | undefined = isOrganizationTeams
    ? { label: t('common:nav.organizations'), href: '/admin/organizations' }
    : undefined;
  const sectionHref = isOrganizationTeams
    ? '/' + segments.slice(0, 4).join('/')
    : location.includes('/settings/stage-collection')
      ? '/admin/settings/stage-collection'
      : location.includes('/settings/stages')
        ? '/admin/settings/stages'
        : location.includes('/settings/skills')
          ? '/admin/settings/skills'
          : location.includes('/settings/observable-signals')
            ? '/admin/settings/observable-signals'
            : location.includes('/settings/system-configuration')
              ? '/admin/settings/system-configuration'
              : location.includes('/settings/ice-server')
                ? '/admin/settings/ice-server'
                : '/' +
                  [section, sectionHrefOverrides[page] ?? segments[1]].filter(Boolean).join('/');

  // The section crumb points at a real page, not the bare `/user` redirect.
  const sectionRootHref = section ? `/${section}/${SECTION_LANDING_PAGE}` : sectionHref;
  // On an edit route the entity crumb links back to the entity's own detail
  // page — the same URL minus the trailing `/edit`.
  const entityHref = isEditRoute ? '/' + segments.slice(0, -1).join('/') : undefined;

  // Depth and back target come from the URL, never from the assembled crumbs.
  // The entity name arrives asynchronously, so a crumb-count rule leaves the
  // phone header showing the drawer button until the fetch lands and then swaps
  // it for a back button under the user's finger.
  const isChildRoute = segments.length >= 3;
  const currentPath = '/' + segments.join('/');
  const isEntityRoute =
    isChildRoute && currentPath !== sectionHref && !isCreateRoute && !isImportRoute;
  const backTarget = actionTitle && entityHref ? entityHref : sectionHref;
  const backHref = backTarget === currentPath ? '/' + segments.slice(0, -1).join('/') : backTarget;

  return {
    pageTitle,
    parentCrumb,
    sectionLabel,
    actionTitle,
    sectionHref,
    sectionRootHref,
    entityHref,
    isChildRoute,
    isEntityRoute,
    backHref,
  };
}

// ─── TopBar Component ─────────────────────────────────────────────────────────
export const TopBar = memo(function TopBar({ onOpenMobile }: TopBarProps) {
  const { t } = useTranslation();
  const { formatRelativeFromNow } = useDateTimeFormatter();
  const [location, setLocation] = useLocation();
  const user = getCurrentUser();
  const isAdmin = getUserRoles().includes(USER_ROLE_CLAIMS.ADMIN);
  const { organization } = useMeAccess();
  const links = useAccountPaths();
  const headerSubtitle = organization?.name
    ? organization.name
    : isAdmin
      ? t('common:nav.administrator')
      : t('common:nav.user');

  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const notificationRef = useRef<HTMLDivElement>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const overflowRef = useRef<HTMLDivElement>(null);

  const {
    pageTitle,
    parentCrumb,
    sectionLabel,
    actionTitle,
    sectionHref,
    sectionRootHref,
    entityHref,
    isChildRoute,
    isEntityRoute,
    backHref: routeBackHref,
  } = usePageMeta(location);
  // Gated on the route: the store still holds the previous page's entity during
  // the 250ms route exit animation, which would otherwise show a persona's name
  // on the scenario list for a quarter of a second.
  const search = useSearch();
  const management = useManagementBreadcrumb(location + search);
  const backHref = management?.backHref ?? routeBackHref;
  const HeaderTitle = management ? 'p' : 'h1';
  const publishedEntityTitle = useBreadcrumbEntityTitle();
  const entityTitle = isChildRoute
    ? publishedEntityTitle || (isEntityRoute ? t('common:topbar.detailTitle') : null)
    : null;

  // Desktop trail. The entity name arrives via usePageBreadcrumbTitle, so this
  // grows a level once the detail page's fetch lands — a label settling, which
  // is why nothing structural is derived from its length.
  const routeCrumbs: Crumb[] = [];
  if (sectionLabel) routeCrumbs.push({ label: sectionLabel, href: sectionRootHref });
  if (parentCrumb) routeCrumbs.push(parentCrumb);
  routeCrumbs.push({ label: pageTitle, href: sectionHref });
  if (entityTitle) routeCrumbs.push({ label: entityTitle, href: entityHref });
  if (actionTitle) routeCrumbs.push({ label: actionTitle });

  // Phone header. Below a list page it takes its "child" form — back + eyebrow +
  // title, global controls folded into an overflow menu so nothing becomes
  // unreachable. Every part of it is URL-derived and therefore correct on the
  // first frame; only the two labels fill in later.
  const crumbs = management?.items ?? routeCrumbs;
  const childTitle = management?.items.at(-1)?.label ?? actionTitle ?? entityTitle ?? pageTitle;
  const childEyebrow =
    management?.items.at(-2)?.label ?? (actionTitle ? (entityTitle ?? pageTitle) : pageTitle);
  // While the entity name is still loading the eyebrow would just repeat the
  // title; show one line rather than the same word twice.
  const showEyebrow = childEyebrow !== childTitle;

  const myRole: UserRole = isAdmin ? 'ADMIN' : 'USER';
  const {
    items: notifications,
    unreadCount,
    markRead: handleMarkRead,
    markAllRead,
  } = useLiveNotifications(myRole);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setNotificationsOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setProfileOpen(false);
      }
      if (overflowRef.current && !overflowRef.current.contains(event.target as Node)) {
        setOverflowOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Expose the LIVE header height (sandbox banner + 64px topbar row) as a CSS
  // var so full-height pages can size with `calc(100vh - var(--app-header-h))`
  // instead of a hardcoded 64px that ignores the conditional sandbox banner
  // (which otherwise pushes their bottom content off-screen). ResizeObserver
  // keeps it correct when the banner toggles or its text wraps.
  const headerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const apply = () =>
      document.documentElement.style.setProperty('--app-header-h', `${el.offsetHeight}px`);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const openAllNotifications = () => {
    setNotificationsOpen(false);
    setLocation(links.notifications);
  };

  const openProfile = () => {
    setProfileOpen(false);
    setLocation(links.profile);
  };

  const openChangePassword = () => {
    setProfileOpen(false);
    setLocation(links.changePassword);
  };

  const handleLogout = async () => {
    setProfileOpen(false);
    await logout();
  };

  const handleNotificationClick = (n: Notification) => {
    handleMarkRead(n.id);
    setNotificationsOpen(false);
    const link = (n.meta as { link?: string } | undefined)?.link;
    if (link) setLocation(link);
  };

  return (
    <div ref={headerRef} className="sticky top-0 z-40 w-full">
      <SandboxNotice />
      <header
        className="w-full h-16 topbar-glass flex items-center px-4 md:px-6"
        data-testid="topbar"
      >
        {/* ── Mobile leading control: drawer on a list page, back on a child ── */}
        {onOpenMobile && !isChildRoute && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onOpenMobile}
            className="mr-2 -ml-2 rounded-xl text-landing-ink-nav hover:bg-tone-glass/50 hover:text-landing-brand-purple md:hidden"
          >
            <Menu className="w-5 h-5" />
          </Button>
        )}
        {isChildRoute && (
          // A link, not history.back(): a page opened from an external link has
          // no history to go back to, and this keeps the browser's own back
          // button meaning what it says.
          <Link
            href={backHref}
            aria-label={t('common:topbar.back', { defaultValue: 'Back' })}
            // 40px box per the design, but padded out to the 44px minimum touch
            // target — the visual size and the tappable size are not the same thing.
            className="mr-2 grid h-11 w-11 flex-shrink-0 place-items-center rounded-xl text-landing-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary md:hidden"
          >
            <span
              className="grid h-10 w-10 place-items-center rounded-xl border bg-tone-surface"
              style={{ borderColor: brandColors.border }}
            >
              <ChevronLeft className="h-5 w-5" />
            </span>
          </Link>
        )}

        {/* ── Breadcrumb (desktop) ── */}
        <nav
          aria-label={t('common:topbar.breadcrumb', { defaultValue: 'Breadcrumb' })}
          className="hidden min-w-0 md:block"
        >
          <ol
            className="flex flex-wrap items-center gap-2"
            style={{ ...insucoachType.crumb, color: brandColors.textSecondary }}
          >
            {crumbs.map((crumb, i) => {
              const isLast = i === crumbs.length - 1;
              return (
                <li key={`${crumb.label}-${i}`} className="flex min-w-0 items-center gap-2">
                  {i > 0 && (
                    <ChevronRight
                      aria-hidden
                      className="h-3.5 w-3.5 flex-shrink-0"
                      style={{ color: brandColors.textTertiary }}
                    />
                  )}
                  {isLast || !crumb.href ? (
                    <span
                      aria-current={isLast ? 'page' : undefined}
                      className="truncate"
                      style={{ color: isLast ? brandColors.textPrimary : undefined }}
                    >
                      {crumb.label}
                    </span>
                  ) : (
                    <Link
                      href={crumb.href}
                      className="truncate transition-colors hover:text-landing-brand-purple focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
                    >
                      {crumb.label}
                    </Link>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
        <HeaderTitle className="sr-only hidden md:block">{childTitle}</HeaderTitle>

        {/* ── Page title (mobile) ── */}
        <div className="min-w-0 flex-1 md:hidden">
          {isChildRoute ? (
            <>
              {showEyebrow && (
                <div
                  className="truncate uppercase"
                  style={{ ...insucoachType.mobileEyebrow, color: brandColors.textTertiary }}
                >
                  {childEyebrow}
                </div>
              )}
              <HeaderTitle
                className="truncate"
                style={{ ...insucoachType.mobileTitle, color: brandColors.textPrimary }}
              >
                {childTitle}
              </HeaderTitle>
            </>
          ) : (
            <HeaderTitle
              className="truncate"
              style={{ ...insucoachType.mobilePageTitle, color: brandColors.textPrimary }}
            >
              {childTitle}
            </HeaderTitle>
          )}
        </div>

        {/* AI badge for coaching pages */}
        {location.includes('coaching') && (
          <motion.span
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="ml-2 hidden items-center gap-1 rounded-full border border-tone-accent-soft-edge/80 bg-tone-accent-soft/90 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-landing-brand-purple md:inline-flex"
          >
            <Sparkles className="w-2.5 h-2.5" />
            AI
          </motion.span>
        )}

        {/* ── Overflow menu — mobile child pages only ──
            The child header hides the global controls per the design. They move
            here rather than disappearing: without this, a learner on a scenario
            detail page has no way to reach notifications, language or sign-out. */}
        {isChildRoute && (
          <div className="relative ml-auto md:hidden" ref={overflowRef}>
            <button
              type="button"
              onClick={() => setOverflowOpen((o) => !o)}
              aria-label={t('common:topbar.moreOptions', { defaultValue: 'More options' })}
              aria-expanded={overflowOpen}
              className="grid h-11 w-11 place-items-center rounded-xl text-landing-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <span
                className="relative grid h-10 w-10 place-items-center rounded-xl border bg-tone-surface"
                style={{ borderColor: brandColors.border }}
              >
                <MoreVertical className="h-5 w-5" />
                {unreadCount > 0 && (
                  <span
                    className="absolute right-2 top-2 h-2 w-2 rounded-full border-2 border-tone-surface"
                    style={{ background: brandColors.danger }}
                  />
                )}
              </span>
            </button>

            <AnimatePresence>
              {overflowOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 6, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 6, scale: 0.96 }}
                  transition={{ duration: 0.15, ease: [0.4, 0, 0.2, 1] }}
                  className="absolute right-0 top-full mt-2 w-56 overflow-hidden rounded-2xl border border-[var(--brand-border)] bg-tone-surface p-1.5 shadow-xl"
                  data-testid="topbar-overflow"
                >
                  <button
                    onClick={() => {
                      setOverflowOpen(false);
                      openAllNotifications();
                    }}
                    className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-[13px] font-medium text-landing-ink-secondary transition-colors hover:bg-muted/50"
                  >
                    <Bell className="h-4 w-4 text-landing-ink-nav" />
                    <span className="flex-1">{t('common:topbar.notifications')}</span>
                    {unreadCount > 0 && (
                      <span
                        className="rounded-full px-1.5 text-[11px] font-bold text-tone-on-accent"
                        style={{ background: brandColors.danger }}
                      >
                        {unreadCount}
                      </span>
                    )}
                  </button>
                  {user && !isAdmin && (
                    <button
                      onClick={() => {
                        setOverflowOpen(false);
                        openProfile();
                      }}
                      className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-[13px] font-medium text-landing-ink-secondary transition-colors hover:bg-muted/50"
                    >
                      <UserIcon className="h-4 w-4 text-landing-ink-nav" />
                      {t('common:nav.profile')}
                    </button>
                  )}
                  {user && (
                    <button
                      onClick={() => {
                        setOverflowOpen(false);
                        openChangePassword();
                      }}
                      className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-[13px] font-medium text-landing-ink-secondary transition-colors hover:bg-muted/50"
                    >
                      <KeyRound className="h-4 w-4 text-landing-ink-nav" />
                      {t('common:nav.changePassword')}
                    </button>
                  )}
                  {user && (
                    <button
                      onClick={() => {
                        setOverflowOpen(false);
                        void handleLogout();
                      }}
                      className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-[13px] font-medium text-tone-negative-solid transition-colors hover:bg-tone-negative-surface hover:text-tone-negative-solid-on-tint"
                    >
                      <LogOut className="h-4 w-4" />
                      {t('common:nav.signOut')}
                    </button>
                  )}
                  {/* Self-hides when the build ships a single language. */}
                  <div className="px-1.5 pt-1.5">
                    <LanguageOptions onPicked={() => setOverflowOpen(false)} />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* ── Right Actions — folded into the overflow menu on child pages ── */}
        <div
          className={cn('ml-auto items-center gap-1.5', isChildRoute ? 'hidden md:flex' : 'flex')}
        >
          {/* Notifications */}
          <div className="relative" ref={notificationRef}>
            <button
              onClick={() => setNotificationsOpen(!notificationsOpen)}
              data-testid="button-notifications"
              style={{
                position: 'relative',
                width: 40,
                height: 40,
                borderRadius: '50%',
                display: 'grid',
                placeItems: 'center',
                background: brandRgba('surfaceCard', 0.62),
                border: `1px solid ${brandColors.border}`,
                color: brandColors.textDeep,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                backdropFilter: 'blur(10px)',
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = brandColors.canvasAlt;
                (e.currentTarget as HTMLButtonElement).style.color = brandColors.brandPrimary;
                (e.currentTarget as HTMLButtonElement).style.borderColor =
                  brandColors.borderStrongAlt;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = brandRgba(
                  'surfaceCard',
                  0.62
                );
                (e.currentTarget as HTMLButtonElement).style.color = brandColors.textDeep;
                (e.currentTarget as HTMLButtonElement).style.borderColor = brandColors.border;
              }}
            >
              <Bell style={{ width: 19, height: 19 }} />
              {unreadCount > 0 && (
                <span
                  style={{
                    position: 'absolute',
                    top: 11,
                    right: 12,
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    background: brandColors.danger,
                    border: '2px solid white',
                  }}
                />
              )}
            </button>

            <AnimatePresence>
              {notificationsOpen && (
                <motion.div
                  initial={{ opacity: 0, y: 6, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 6, scale: 0.96 }}
                  transition={{ duration: 0.15, ease: [0.4, 0, 0.2, 1] }}
                  className={`absolute right-0 top-full mt-2 w-80 overflow-hidden rounded-2xl border border-[var(--brand-elevated-alt)] bg-tone-surface shadow-xl`}
                  data-testid="notifications-dropdown"
                >
                  {/* Header */}
                  <div className="px-4 py-3 border-b border-border/50 flex items-center justify-between bg-muted/30">
                    <div className="flex items-center gap-2">
                      <Bell className="w-3.5 h-3.5 text-muted-foreground" />
                      <h3
                        className="font-semibold text-foreground text-[13px]"
                        style={{ fontFamily: 'var(--font-display)' }}
                      >
                        {t('common:topbar.notifications')}
                      </h3>
                    </div>
                    {unreadCount > 0 && (
                      <button
                        onClick={markAllRead}
                        className="text-[11px] text-primary font-medium hover:underline"
                      >
                        {t('common:topbar.markAllRead', { defaultValue: 'Mark all as read' })}
                      </button>
                    )}
                  </div>

                  {/* Notification List */}
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="p-8 text-center">
                        <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-muted flex items-center justify-center">
                          <Bell className="w-5 h-5 text-muted-foreground/60" />
                        </div>
                        <p className="text-[13px] text-muted-foreground font-medium">
                          {t('common:topbar.noNotifications')}
                        </p>
                      </div>
                    ) : (
                      notifications.slice(0, 5).map((notification, index) => {
                        const nMeta = notification.meta as
                          | { titleKey?: string; messageKey?: string; params?: TranslationParams }
                          | undefined;
                        const nTitle = nMeta?.titleKey
                          ? t(nMeta.titleKey as any, nMeta.params)
                          : notification.title;
                        const nMessage = nMeta?.messageKey
                          ? t(nMeta.messageKey as any, nMeta.params)
                          : notification.message;
                        return (
                          <motion.div
                            key={notification.id}
                            initial={{ opacity: 0, x: -8 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.04 }}
                            className={cn(
                              'px-4 py-3 border-b border-border/30 last:border-0 hover:bg-muted/40 cursor-pointer transition-colors duration-150',
                              !notification.read && 'bg-primary/5'
                            )}
                            onClick={() => handleNotificationClick(notification)}
                            data-testid={`notification-item-${notification.id}`}
                          >
                            <div className="flex items-start gap-3">
                              <div
                                className={cn(
                                  'w-2 h-2 rounded-full mt-1.5 flex-shrink-0',
                                  notification.read
                                    ? 'bg-muted-foreground/30'
                                    : severityDot[notification.severity]
                                )}
                              />
                              <div className="flex-1 min-w-0">
                                <p
                                  className={cn(
                                    'text-[13px] truncate',
                                    notification.read
                                      ? 'font-medium text-foreground/70'
                                      : 'font-semibold text-foreground'
                                  )}
                                >
                                  {nTitle}
                                </p>
                                <p className="text-[11px] text-muted-foreground line-clamp-2 mt-0.5 leading-relaxed">
                                  {nMessage}
                                </p>
                                <p className="text-[10.5px] text-muted-foreground/60 mt-1">
                                  {formatRelativeFromNow(notification.createdAt)}
                                </p>
                              </div>
                            </div>
                          </motion.div>
                        );
                      })
                    )}
                  </div>
                  {notifications.length > 0 && (
                    <button
                      onClick={openAllNotifications}
                      className="w-full px-4 py-2.5 text-xs text-primary font-medium hover:bg-muted/40 transition-colors border-t border-border/30 text-center rounded-b-2xl"
                    >
                      {t('common:topbar.viewAll')}
                    </button>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Language toggle — desktop only; on mobile it lives in the side panel.
              Self-hides when the account may use only one language. */}
          <div className="hidden md:block">
            <LanguageToggle />
          </div>

          {/* ── Profile Section — desktop only (hidden on mobile) ── */}
          {user && (
            <div className="relative ml-1.5 hidden md:block" ref={profileRef}>
              <button
                onClick={() => setProfileOpen((o) => !o)}
                className="flex items-center gap-2.5 border-l border-tone-accent-hairline/80 pl-3"
                data-testid="button-profile-menu"
              >
                {/* User info — desktop */}
                <div className="text-right">
                  <p className="text-[13px] font-semibold leading-tight tracking-[-0.01em] text-landing-ink">
                    {user.name.split(' ')[0]}
                  </p>
                  <p
                    className="text-[10px] font-medium leading-tight text-landing-ink-nav"
                    data-testid="header-subtitle"
                  >
                    {headerSubtitle}
                  </p>
                </div>

                {/* Avatar */}
                <div className="relative status-dot">
                  <Avatar className="h-8 w-8 ring-2 ring-primary/20 ring-offset-1 ring-offset-background transition-all hover:ring-primary/40">
                    <AvatarFallback
                      className="text-[11px] font-bold"
                      style={{
                        background: landingGradients.logo,
                        color: 'white',
                      }}
                    >
                      {user.name
                        .split(' ')
                        .map((n) => n[0])
                        .join('')}
                    </AvatarFallback>
                  </Avatar>
                </div>

                <ChevronDown
                  className={cn(
                    'h-3.5 w-3.5 text-landing-ink-nav transition-transform',
                    profileOpen && 'rotate-180'
                  )}
                />
              </button>

              <AnimatePresence>
                {profileOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 6, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 6, scale: 0.96 }}
                    transition={{ duration: 0.15, ease: [0.4, 0, 0.2, 1] }}
                    className={`absolute right-0 top-full mt-2 w-52 overflow-hidden rounded-2xl border border-[var(--brand-elevated-alt)] bg-tone-surface p-1.5 shadow-xl`}
                    data-testid="profile-dropdown"
                  >
                    {!isAdmin && (
                      <button
                        onClick={openProfile}
                        className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-[13px] font-medium text-landing-ink-secondary transition-colors hover:bg-muted/50"
                        data-testid="button-view-profile"
                      >
                        <UserIcon className="h-4 w-4 text-landing-ink-nav" />
                        {t('common:nav.profile')}
                      </button>
                    )}
                    <button
                      onClick={openChangePassword}
                      className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-[13px] font-medium text-landing-ink-secondary transition-colors hover:bg-muted/50"
                      data-testid="button-change-password-menu"
                    >
                      <KeyRound className="h-4 w-4 text-landing-ink-nav" />
                      {t('common:nav.changePassword')}
                    </button>
                    <button
                      onClick={() => void handleLogout()}
                      className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-left text-[13px] font-medium text-tone-negative-solid transition-colors hover:bg-tone-negative-surface hover:text-tone-negative-solid-on-tint"
                      data-testid="button-logout"
                    >
                      <LogOut className="h-4 w-4" />
                      {t('common:nav.signOut')}
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>
      </header>
    </div>
  );
});
